import { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { CONTACT } from "@/constants/testIds";
import { Reveal } from "@/components/Reveal";
import { toast } from "sonner";

export default function Contatti() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", message: "" });
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [submittedName, setSubmittedName] = useState("");

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!consent) return;
    setLoading(true);
    try {
      await api.post("/contact", form);
      setSubmittedName(form.name.trim());
      setSuccess(true);
      setForm({ name: "", email: "", phone: "", company: "", message: "" });
      setConsent(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-cream grain pt-32 pb-24">
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
        <div className="grid grid-cols-12 gap-6">
          <div className="col-span-12 md:col-span-3">
            <p className="text-xs uppercase tracking-[0.3em] text-ink/60">— Contatti</p>
          </div>
          <div className="col-span-12 md:col-span-9">
            <Reveal>
              <h1 className="font-heading text-6xl md:text-[9rem] leading-[0.85] tracking-tighter">
                Dimmi<br /><span className="italic text-peach">il perché.</span>
              </h1>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 mt-24 border-t border-line pt-16">
        <div className="grid grid-cols-12 gap-6 md:gap-16">
          <div className="col-span-12 md:col-span-4 space-y-10">
            <Reveal>
              <div>
                <p className="text-xs uppercase tracking-[0.3em] text-ink/60 mb-2">Email</p>
                <a href="mailto:giovanni.gabban@gmail.com" className="font-heading text-3xl editorial-link">
                  giovanni.gabban@gmail.com
                </a>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <div>
                <p className="text-xs uppercase tracking-[0.3em] text-ink/60 mb-2">Social</p>
                <a
                  href="https://www.linkedin.com/in/giovannigabban/"
                  target="_blank"
                  rel="noreferrer"
                  className="font-heading text-3xl editorial-link"
                >
                  LinkedIn
                </a>
              </div>
            </Reveal>
            <Reveal delay={0.2}>
              <div>
                <p className="text-xs uppercase tracking-[0.3em] text-ink/60 mb-2">Tempo di risposta</p>
                <p className="font-heading text-2xl italic text-ink/80">Entro 48 ore lavorative.</p>
              </div>
            </Reveal>
          </div>

          <div className="col-span-12 md:col-span-8">
            {success ? (
              <Reveal>
                <div
                  data-testid={CONTACT.success}
                  className="border-t border-line pt-10 md:pt-12"
                >
                  <p className="text-xs uppercase tracking-[0.3em] text-peach mb-6">— Messaggio ricevuto</p>
                  <h2 className="font-heading text-5xl md:text-7xl tracking-tighter leading-[0.95] text-ink">
                    Grazie, {submittedName}!
                  </h2>
                  <p className="font-heading italic text-3xl md:text-5xl text-peach mt-4 leading-tight">
                    Ho ricevuto il tuo messaggio.<br />
                    Ti contatto entro 48 ore lavorative.
                  </p>
                  <button
                    type="button"
                    onClick={() => setSuccess(false)}
                    data-testid="contact-send-another"
                    className="mt-10 inline-flex items-center gap-3 text-xs uppercase tracking-[0.22em] editorial-link text-ink/70"
                  >
                    ← Invia un altro messaggio
                  </button>
                </div>
              </Reveal>
            ) : (
            <form onSubmit={submit} data-testid={CONTACT.form} className="space-y-10">
              <div>
                <label className="block text-xs uppercase tracking-[0.22em] text-ink/60 mb-3">Nome *</label>
                <input
                  data-testid={CONTACT.name}
                  required
                  value={form.name}
                  onChange={update("name")}
                  className="w-full bg-transparent border-b border-line-dark py-4 focus:outline-none focus:border-peach transition-colors text-lg"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-10">
                <div>
                  <label className="block text-xs uppercase tracking-[0.22em] text-ink/60 mb-3">Email *</label>
                  <input
                    data-testid={CONTACT.email}
                    type="email"
                    required
                    value={form.email}
                    onChange={update("email")}
                    className="w-full bg-transparent border-b border-line-dark py-4 focus:outline-none focus:border-peach transition-colors text-lg"
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-[0.22em] text-ink/60 mb-3">Telefono</label>
                  <input
                    data-testid="contact-phone"
                    type="tel"
                    value={form.phone}
                    onChange={update("phone")}
                    className="w-full bg-transparent border-b border-line-dark py-4 focus:outline-none focus:border-peach transition-colors text-lg"
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-[0.22em] text-ink/60 mb-3">Azienda</label>
                  <input
                    data-testid={CONTACT.company}
                    value={form.company}
                    onChange={update("company")}
                    className="w-full bg-transparent border-b border-line-dark py-4 focus:outline-none focus:border-peach transition-colors text-lg"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs uppercase tracking-[0.22em] text-ink/60 mb-3">Raccontami brevemente *</label>
                <textarea
                  data-testid={CONTACT.message}
                  required
                  rows={5}
                  value={form.message}
                  onChange={update("message")}
                  className="w-full bg-transparent border-b border-line-dark py-4 focus:outline-none focus:border-peach transition-colors text-lg resize-none"
                />
              </div>
              <label className="flex items-start gap-3 cursor-pointer group select-none">
                <input
                  type="checkbox"
                  required
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  data-testid="contact-consent"
                  className="mt-1 h-4 w-4 accent-peach cursor-pointer shrink-0"
                />
                <span className="text-sm text-ink/70 leading-relaxed">
                  Ho letto e accetto la{" "}
                  <Link to="/privacy-policy" className="editorial-link text-peach">
                    Privacy Policy
                  </Link>
                  .
                </span>
              </label>
              <div className="flex items-center gap-6 flex-wrap">
                <button
                  type="submit"
                  disabled={loading || !consent}
                  data-testid={CONTACT.submit}
                  className="bg-ink text-cream rounded-full px-10 py-5 text-xs uppercase tracking-[0.22em] hover:bg-peach transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-ink"
                >
                  {loading ? "Invio…" : "Invia messaggio"}
                </button>
              </div>
            </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
